import type { ReactNode } from 'react';

import type { IFeatureMap, TFlags } from '@feathr/blackbox';
import { useCachedBlackboxVersion } from '@feathr/extender/hooks';
import { useMinPackageForFeature } from '@feathr/extender/hooks';

import { useAccount, useFlags } from '../../state';

interface IBaseProps {
  /**
   * The content to render when the flag or license feature is enabled.
   */
  children: ReactNode;
  /**
   * Whether to devOverride the flag/license check.
   */
  devOverride?: boolean;
}

interface IFeatureFlagProps extends IBaseProps {
  /**
   * The feature flag name from EFlags to check.
   * If the flag is true, children will be rendered.
   * If the flag is false or undefined, nothing will be rendered.
   */
  featureName: TFlags;
  licenseFeature?: never;
  licenseFeatureCategory?: never;
}

interface ILicenseFeatureProps extends IBaseProps {
  featureName?: never;
  /**
   * The license feature name to check (e.g., 'showOnboarding').
   * If the user has access to this feature, children will be rendered.
   * If the user doesn't have access, nothing will be rendered.
   */
  licenseFeature: string;
  /**
   * The feature category to check within (optional, will search all categories if not provided).
   */
  licenseFeatureCategory?: keyof IFeatureMap;
}

interface IBothProps extends IBaseProps {
  /**
   * The feature flag name from EFlags to check.
   */
  featureName: TFlags;
  /**
   * The license feature name to check (e.g., 'showOnboarding').
   */
  licenseFeature: string;
  /**
   * The feature category to check within (optional, will search all categories if not provided).
   */
  licenseFeatureCategory?: keyof IFeatureMap;
}

type IProps = IFeatureFlagProps | ILicenseFeatureProps | IBothProps;

/**
 * A component that conditionally renders its children based on a feature flag or license feature.
 * This component can check both feature flags and license-based permissions.
 *
 * @example
 * ```tsx
 * import { Flaggable } from '@feathr/extender/components';
 *
 * // Feature flag check
 * <Flaggable featureName="showForms">
 *   <FormsComponent />
 * </Flaggable>
 *
 * // License feature check
 * <Flaggable licenseFeature="showOnboarding">
 *   <PixlPlusComponent />
 * </Flaggable>
 *
 * // Both checks (either can grant access)
 * <Flaggable featureName="showAdvanced" licenseFeature="advancedFeatures">
 *   <AdvancedComponent />
 * </Flaggable>
 * ```
 */
function Flaggable({
  featureName,
  licenseFeature,
  licenseFeatureCategory,
  children,
  devOverride,
}: Readonly<IProps>): ReactNode {
  const flags = useFlags();
  const account = useAccount();
  const { isOnDevelopment } = useCachedBlackboxVersion();

  // Check feature flag
  const isFlagEnabled = featureName ? Boolean(flags[featureName]) : false;

  // Check license feature (always call hook, but conditionally use result)
  const minPackageRequired = useMinPackageForFeature(
    account?.activePackage,
    licenseFeature || '',
    licenseFeatureCategory,
  );
  const hasLicenseAccess = licenseFeature ? minPackageRequired === null : false;

  // Determine if any condition grants access
  const hasAccess = isFlagEnabled || hasLicenseAccess;

  // Render children if:
  // 1. Feature flag is enabled, OR
  // 2. User has license access to the feature, OR
  // 3. devOverride is true AND we're on development
  if (hasAccess || (devOverride && isOnDevelopment)) {
    return children;
  }

  return null;
}

export default Flaggable;
